/**
 * @file LeadsTab.jsx
 * @description Componente que integra os filtros e a tabela de leads para o sistema SDR Virtual
 * 
 * Este componente serve como um contêiner para a funcionalidade de gerenciamento de leads,
 * conectando o componente de filtros com a tabela que exibe os resultados filtrados.
 * Implementa filtros dinâmicos onde os motivos de descarte se atualizam conforme outros filtros.
 */

import { Alert, Box } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import LeadsFilters from '../Filters/LeadsFilters';
import LeadsTableSDRVirtual from '../Table/LeadsTableSDRVirtual';
import LoadingTable from '../Table/LoadingTable';
import { exportLeadsToXlsx } from '../../utils/helpers/exportLeadsToXlsx';

/**
 * Componente de aba para visualização e filtragem de leads
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.allLeads - Array completo de todos os leads (sem filtros aplicados)
 * @param {Array} props.filteredLeads - Array de leads já filtrados para exibição na tabela
 * @param {string|number} props.idLeadFilter - Valor atual do filtro de ID de lead
 * @param {string} props.nomeFilter - Valor atual do filtro de nome
 * @param {string} props.whatsAppIdFilter - Valor atual do filtro de WhatsApp ID
 * @param {string} props.situacaoFilter - Valor atual do filtro de situação do lead
 * @param {string} props.descarteFilter - Valor atual do filtro de motivo de descarte
 * @param {Function} props.handleClearFilters - Função para limpar todos os filtros
 * @param {Function} props.setIdLeadFilter - Função para atualizar o filtro de ID
 * @param {Function} props.setNomeFilter - Função para atualizar o filtro de nome
 * @param {Function} props.setWhatsAppIdFilter - Função para atualizar o filtro de WhatsApp ID
 * @param {Function} props.setSituacaoFilter - Função para atualizar o filtro de situação
 * @param {Function} props.setDateFilter - Função para atualizar o filtro de data
 * @param {Function} props.setDescarteFilter - Função para atualizar o filtro de descarte
 * @param {Function} props.handleWhatsAppIdClick - Função para lidar com clique no WhatsApp ID na tabela
 * @param {Function} props.formatDate - Função para formatar datas para exibição
 * @param {number} props.loadingDelay - Tempo de delay para o loading em ms (padrão: 800ms)
 * @returns {JSX.Element} Contêiner com componentes de filtro e tabela de leads
 */
const LeadsTab = ({
    filteredLeads = [],
    idLeadFilter,
    nomeFilter,
    whatsAppIdFilter,
    situacaoFilter,
    descarteFilter,
    tempIdLeadFilter,        // Nova prop
    tempNomeFilter,          // Nova prop
    tempWhatsAppIdFilter,    // Nova prop
    tempSituacaoFilter,      // Nova prop
    tempDescarteFilter,      // Nova prop
    tempEmpreendimentoFilter,
    setTempEmpreendimentoFilter,
    empreendimentoFilter,
    empreendimentosDisponiveis = [],
    tempStartDate,           // Nova prop
    tempEndDate,             // Nova prop
    tempDateFilter,          // Nova prop
    setTempIdLeadFilter,     // Nova prop
    setTempNomeFilter,       // Nova prop
    setTempWhatsAppIdFilter, // Nova prop
    setTempSituacaoFilter,   // Nova prop
    setTempDescarteFilter,   // Nova prop
    setTempStartDate,        // Nova prop
    setTempEndDate,          // Nova prop
    setTempDateFilter,       // Nova prop
    handleApplyFilters,      // Nova prop
    handleClearFilters,
    setIdLeadFilter,
    setNomeFilter,
    setWhatsAppIdFilter,
    setSituacaoFilter,
    setDescarteFilter, // Função para atualizar o filtro de descarte
    handleWhatsAppIdClick,
    formatDate,
    setForceTokenExpired,
    loadingDelay = 800, // Tempo padrão de loading
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    dateFilter,
    setDateFilter,
    motivosDescarte = [],
    isInitialLoading = false,
    viewMode,
    setViewMode,
    situacoesDisponiveis = [],
    onYearChange,              // NOVA prop
    idCRM,                     // NOVA prop
}) => {

    // Estado para controlar o loading da tabela
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [exportError, setExportError] = useState('');

    // Estado para armazenar os valores anteriores dos filtros
    const previousFiltersRef = useRef({
        idLeadFilter,
        nomeFilter,
        whatsAppIdFilter,
        situacaoFilter,
        descarteFilter,
        startDate,
        endDate,
        dateFilter
    });

    // useEffect para detectar mudanças nos filtros
    useEffect(() => {
        const currentFilters = {
            idLeadFilter,
            nomeFilter,
            whatsAppIdFilter,
            situacaoFilter,
            descarteFilter,
            startDate,
            endDate,
            dateFilter
        };

        const hasFilterChanged = Object.keys(currentFilters).some(
            key => currentFilters[key] !== previousFiltersRef.current[key]
        );

        if (hasFilterChanged) {
            setIsTableLoading(true);

            const loadingTimeout = setTimeout(() => {
                setIsTableLoading(false);
            }, loadingDelay);

            // Atualiza os filtros anteriores
            previousFiltersRef.current = currentFilters;

            return () => {
                clearTimeout(loadingTimeout);
            };
        }
    }, [idLeadFilter, nomeFilter, whatsAppIdFilter, situacaoFilter, descarteFilter, startDate, endDate, dateFilter, loadingDelay, setForceTokenExpired]);

    // Função wrapper para limpar filtros que também ativa o loading
    const handleClearFiltersWithLoading = () => {
        setIsTableLoading(true);
        handleClearFilters();

        setTimeout(() => {
            setIsTableLoading(false);
        }, loadingDelay);
    };

    const handleExportLeads = () => {
        try {
            const result = exportLeadsToXlsx({
                leads: filteredLeads,
                formatDate,
                fileNamePrefix: 'leads-filtrados',
                idCRM,
                filters: {
                    situacaoFilter,
                    descarteFilter,
                    idLeadFilter,
                    nomeFilter,
                    whatsAppIdFilter,
                    empreendimentoFilter,
                    dateFilter,
                    startDate,
                    endDate,
                },
            });

            if (!result?.ok) {
                setExportError(result?.error || 'Não foi possível exportar a planilha. Tente novamente.');
                return;
            }

            setExportError('');
        } catch (error) {
            console.error('Erro ao exportar planilha:', error);
            setExportError('Não foi possível exportar a planilha. Tente novamente.');
        }
    };

    return (
        // Contêiner principal com padding responsivo e fundo suave
        <Box
            sx={{
                mt: { xs: 2, md: 3 },
                mb: { xs: 3, md: 4 },
                minHeight: '100vh',
            }}
        >
            {exportError && (
                <Alert
                    severity="error"
                    onClose={() => setExportError('')}
                    sx={{ mb: 2 }}
                >
                    {exportError}
                </Alert>
            )}
            {/* 
              Componente de filtros que recebe todos os estados dos filtros,
              as funções para atualizá-los e a contagem total de leads filtrados.
              Os motivos de descarte são calculados dinamicamente.
            */}
            <LeadsFilters
                situacaoFilter={tempSituacaoFilter}      // Mudança
                setSituacaoFilter={setTempSituacaoFilter} // Mudança
                descarteFilter={tempDescarteFilter}       // Mudança
                setDescarteFilter={setTempDescarteFilter} // Mudança
                empreendimentoFilter={tempEmpreendimentoFilter}
                setEmpreendimentoFilter={setTempEmpreendimentoFilter}
                empreendimentosDisponiveis={empreendimentosDisponiveis}
                idLeadFilter={tempIdLeadFilter}           // Mudança
                setIdLeadFilter={setTempIdLeadFilter}     // Mudança
                nomeFilter={tempNomeFilter}               // Mudança
                setNomeFilter={setTempNomeFilter}         // Mudança
                whatsAppIdFilter={tempWhatsAppIdFilter}   // Mudança
                setWhatsAppIdFilter={setTempWhatsAppIdFilter} // Mudança
                handleClearFilters={handleClearFiltersWithLoading}
                totalFiltered={filteredLeads.length}
                startDate={tempStartDate}                 // Mudança
                setStartDate={setTempStartDate}           // Mudança
                endDate={tempEndDate}                     // Mudança
                setEndDate={setTempEndDate}               // Mudança
                dateFilter={tempDateFilter}               // Mudança
                setDateFilter={setTempDateFilter}         // Mudança
                handleApplyFilters={handleApplyFilters}   // Nova prop
                motivosDescarte={motivosDescarte}
                situacoesDisponiveis={situacoesDisponiveis}
                onYearChange={onYearChange}    // NOVO
                onExportLeads={handleExportLeads}
                exportDisabled={false}
            />


            {/* Container da tabela com estilização moderna */}
            {(isTableLoading || isInitialLoading) ? (
                <LoadingTable />
            ) : (
                <LeadsTableSDRVirtual
                    leads={filteredLeads}
                    handleWhatsAppIdClick={handleWhatsAppIdClick}
                    formatDate={formatDate}
                    setForceTokenExpired={setForceTokenExpired}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    dateFilter={dateFilter}
                    startDate={startDate}
                    endDate={endDate}
                />
            )}


        </Box>
    );
};

export default LeadsTab;
